import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CaretRight, CheckCircle, PlusCircle } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/Button";
import { usePageMeta } from "@/hooks/usePageMeta";
import { NotFound } from "./NotFound";
import {
  findUseCaseBySlug,
  slugFor,
  USE_CASES,
  type UseCase,
} from "./useCases";
import { getUseCaseContent } from "./useCasesContent";
import styles from "./UseCasePage.module.css";

export function UseCasePage() {
  const { slug } = useParams<{ slug: string }>();
  const useCase = findUseCaseBySlug(slug);

  if (!useCase) return <NotFound />;
  return <UseCaseView useCase={useCase} />;
}

function UseCaseView({ useCase }: { useCase: UseCase }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "de";
  const content = getUseCaseContent(useCase.id, lang);

  usePageMeta({
    title: content?.title ?? useCase.id,
    description: content?.tagline,
  });

  if (!content) return <NotFound />;

  const { icon: Icon, tone } = useCase;

  const otherUseCases = USE_CASES.filter((u) => u.id !== useCase.id).slice(0, 6);

  return (
    <article className={styles.page}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/" className={styles.breadcrumbLink}>
          {t("nav.home")}
        </Link>
        <CaretRight size={12} weight="bold" aria-hidden />
        <Link to="/usefor" className={styles.breadcrumbLink}>
          {t("useCases.indexTitle")}
        </Link>
        <CaretRight size={12} weight="bold" aria-hidden />
        <span aria-current="page">{content.shortLabel}</span>
      </nav>

      <header className={`${styles.hero} ${styles[`hero_${tone}`]}`}>
        <div className={styles.heroIcon}>
          <Icon size={44} weight="regular" />
        </div>
        <div className={styles.heroText}>
          <h1 className={styles.heroTitle}>{content.title}</h1>
          <p className={styles.heroTagline}>{content.tagline}</p>
        </div>
      </header>

      <p className={styles.intro}>{content.intro}</p>
      <p className={styles.typical}>{content.typical}</p>

      <section className={styles.section}>
        <h2>{t("useCases.examplesTitle")}</h2>
        <ul className={styles.examplesList}>
          {content.examples.map((e, i) => (
            <li key={i} className={styles.exampleItem}>
              <span>{e}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.section}>
        <h2>{t("useCases.itemIdeasTitle")}</h2>
        <p className={styles.sectionLead}>{t("useCases.itemIdeasLead")}</p>
        <ul className={styles.itemGrid}>
          {content.itemIdeas.map((item, i) => (
            <li key={i} className={styles.itemChip}>
              <CheckCircle size={14} weight="fill" aria-hidden className={styles.itemCheck} />
              {item}
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.section}>
        <h2>{t("useCases.tipsTitle")}</h2>
        <ul className={styles.tipsList}>
          {content.tips.map((tip, i) => (
            <li key={i} className={styles.tipItem}>
              <span className={styles.tipNumber}>{i + 1}</span>
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.ctaBlock}>
        <h2 className={styles.ctaTitle}>
          {t("useCases.ctaTitle", { name: content.shortLabel })}
        </h2>
        <Link to="/new" className={styles.ctaLink}>
          <Button className={styles.ctaButton}>
            <PlusCircle size={20} weight="regular" />
            {t("useCases.ctaButton")}
          </Button>
        </Link>
        <p className={styles.ctaHints}>{t("useCases.ctaHints")}</p>
      </section>

      <section className={styles.otherCases}>
        <h2>{t("useCases.otherTitle")}</h2>
        <ul className={styles.otherList}>
          {otherUseCases.map((other) => {
            const OtherIcon = other.icon;
            const otherContent = getUseCaseContent(other.id, lang);
            if (!otherContent) return null;
            return (
              <li key={other.id}>
                <Link
                  to={`/usefor/${slugFor(other, lang)}`}
                  className={`${styles.otherChip} ${styles[`otherChip_${other.tone}`]}`}
                >
                  <OtherIcon size={20} weight="regular" aria-hidden />
                  <span>{otherContent.shortLabel}</span>
                </Link>
              </li>
            );
          })}
        </ul>
        <Link to="/usefor" className={styles.allLink}>
          <ArrowLeft size={14} weight="bold" aria-hidden /> {t("useCases.allLink")}
        </Link>
      </section>
    </article>
  );
}
